/**
 * Flow editor (create / edit) — beginner-friendly visual builder with a live phone
 * preview. No JSON required; the Flow JSON is generated behind the scenes (and shown
 * read-only under "Advanced" for power users).
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { IconDeviceFloppy, IconCode, IconInfoCircle } from '@tabler/icons-react';
import {
  Stack, Group, Text, TextInput, Textarea, Select, Button, Alert, Loader, Collapse, Code, SegmentedControl, SimpleGrid, ScrollArea,
} from '../../../../../components/ui';
import { Card } from '../../../../../components/ui/data-display';
import { ChannelPage } from '../../_shared/ChannelPage';
import { FlowScreensEditor } from './FlowScreensEditor';
import { FlowScreenPreview } from '../../_shared/FlowScreenPreview';
import {
  type FlowScreen, newScreen, parseDefinition, serializeDefinition, generateFlowJson, FLOW_CATEGORIES, FLOW_TEMPLATES,
} from '../../_shared/flowDefinition';
import { useWhatsAppFlows } from '../../_shared/useWhatsAppFlows';

const BASE = '/admin/communications/whatsapp/flows';

export function FlowEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [search] = useSearchParams();
  const editingId = id ? Number(id) : null;
  const { saving, getFlow, createFlow, updateFlow } = useWhatsAppFlows();

  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [screens, setScreens] = useState<FlowScreen[]>(() => [newScreen(0)]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showJson, setShowJson] = useState(false);
  const [nameError, setNameError] = useState(false);

  // Initial load: edit existing, start from a template, or blank.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (editingId) {
        setLoading(true);
        const flow = await getFlow(editingId);
        if (cancelled) return;
        if (flow) {
          setName(flow.name);
          setDescription(flow.description ?? '');
          setCategory(flow.category ?? null);
          const sc = parseDefinition(flow.definition);
          setScreens(sc);
          setActiveId(sc[0]?.id ?? null);
        }
        setLoading(false);
      } else {
        const tplKey = search.get('template');
        const tpl = FLOW_TEMPLATES.find((t) => t.key === tplKey);
        if (tpl) {
          const sc = tpl.build();
          setScreens(sc);
          setActiveId(sc[0]?.id ?? null);
          setName(tpl.name);
          setCategory(tpl.category);
        } else {
          const sc = [newScreen(0)];
          setScreens(sc);
          setActiveId(sc[0].id);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [editingId, getFlow, search]);

  const activeScreen = useMemo(() => screens.find((s) => s.id === activeId) ?? screens[0], [screens, activeId]);
  const activeIndex = screens.findIndex((s) => s.id === activeScreen?.id);
  const flowJson = useMemo(() => generateFlowJson(screens), [screens]);

  const handleSave = async () => {
    if (!name.trim()) { setNameError(true); return; }
    const body = {
      name: name.trim(),
      description: description.trim() || null,
      category,
      definition: serializeDefinition(screens),
      flowJson,
      screenCount: screens.length,
    };
    const result = editingId ? await updateFlow(editingId, body) : await createFlow(body);
    if (result) navigate(`${BASE}/${result.flowId}`);
  };

  if (loading) {
    return (
      <ChannelPage icon={<IconDeviceFloppy size={28} />} title={editingId ? 'Edit flow' : 'New flow'} onBack={() => navigate(BASE)}>
        <Group justify="center" p="xl"><Loader size="lg" /></Group>
      </ChannelPage>
    );
  }

  return (
    <ChannelPage
      icon={<IconDeviceFloppy size={28} />}
      title={editingId ? 'Edit flow' : 'New flow'}
      subtitle="Build screens visually — the preview updates as you go."
      onBack={() => navigate(editingId ? `${BASE}/${editingId}` : BASE)}
      actions={<Button leftSection={<IconDeviceFloppy size={16} />} onClick={handleSave} loading={saving} gradientVariant="primary">Save flow</Button>}
    >
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
        {/* Left: meta + screens */}
        <Stack gap="md">
          <Card withBorder padding="md">
            <Stack gap="sm">
              <TextInput
                label="Flow name"
                placeholder="e.g. Event sign-up"
                required
                value={name}
                error={nameError ? 'Name is required' : undefined}
                onChange={(e) => { setName(e.currentTarget.value); if (nameError) setNameError(false); }}
              />
              <Textarea label="Description" placeholder="What is this flow for? (internal note)" autosize minRows={1} value={description} onChange={(e) => setDescription(e.currentTarget.value)} />
              <Select label="Category" placeholder="Optional" clearable data={FLOW_CATEGORIES.map((c) => ({ value: c, label: c.replace(/_/g, ' ') }))} value={category} onChange={setCategory} />
            </Stack>
          </Card>

          <Card withBorder padding="md">
            <FlowScreensEditor screens={screens} onChange={setScreens} activeId={activeScreen?.id ?? null} onSelect={setActiveId} />
          </Card>
        </Stack>

        {/* Right: live preview + advanced JSON */}
        <Stack gap="md" style={{ position: 'sticky', top: 12, alignSelf: 'flex-start' }}>
          <Card withBorder padding="md">
            <Group justify="space-between" mb="sm">
              <Text size="sm" fw={500}>Live preview</Text>
              {screens.length > 1 && (
                <SegmentedControl
                  size="xs"
                  value={activeScreen?.id}
                  onChange={setActiveId}
                  data={screens.map((s, i) => ({ value: s.id, label: String(i + 1) }))}
                />
              )}
            </Group>
            {activeScreen && (
              <FlowScreenPreview screen={activeScreen} flowName={name || 'WhatsApp Flow'} screenIndex={activeIndex} screenTotal={screens.length} />
            )}
          </Card>

          <Card withBorder padding="sm">
            <Group justify="space-between">
              <Group gap="xs"><IconCode size={16} /><Text size="sm" fw={500}>Advanced: Flow JSON</Text></Group>
              <Button size="xs" variant="subtle" onClick={() => setShowJson((v) => !v)}>{showJson ? 'Hide' : 'Show'}</Button>
            </Group>
            <Collapse in={showJson}>
              <Alert color="gray" icon={<IconInfoCircle size={14} />} mt="xs">
                Generated automatically — you don't need to edit this. It's what you'll publish in the hub.
              </Alert>
              <ScrollArea h={300} mt="xs">
                <Code block style={{ fontSize: 11 }}>{flowJson}</Code>
              </ScrollArea>
            </Collapse>
          </Card>
        </Stack>
      </SimpleGrid>
    </ChannelPage>
  );
}
